import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ChatWidget.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [conversationExists, setConversationExists] = useState(false);
  const [status, setStatus] = useState('AGENT');
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const pollingInterval = useRef(null);

  const pollMessages = useCallback(async () => {
    if (!sessionId || !conversationExists) return;
    try {
      const response = await fetch(`${API_URL}/chat/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      });
      const data = await response.json();
      if (data.messages) {
        setMessages(data.messages);
        if (!isOpen && data.messages.length > messages.length) {
          setUnreadCount(prev => prev + (data.messages.length - messages.length));
        }
      }
      if (data.conversation?.status) {
        setStatus(data.conversation.status);
      }
    } catch (error) {
      console.error('Error polling messages:', error);
    }
  }, [sessionId, conversationExists, isOpen, messages.length]);

  // Initialize session — only load existing conversation, don't create one
  useEffect(() => {
    let storedSessionId = localStorage.getItem('chatSessionId');
    if (!storedSessionId) {
      storedSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chatSessionId', storedSessionId);
    }
    setSessionId(storedSessionId);
    checkExistingConversation(storedSessionId);
  }, []);

  // Check if a conversation already exists without creating one
  const checkExistingConversation = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/chat/conversation/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid })
      });
      const data = await response.json();
      if (data.exists) {
        setConversationExists(true);
        setMessages(data.messages || []);
        setStatus(data.conversation?.status || 'AGENT');
      }
    } catch (error) {
      console.error('Error checking conversation:', error);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Poll for new messages when conversation is assigned to staff
  useEffect(() => {
    if (isOpen && conversationExists && (status === 'ASSIGNED' || status === 'WAITING')) {
      pollingInterval.current = setInterval(() => {
        pollMessages();
      }, 3000);
    } else {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    }
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [isOpen, conversationExists, status, pollMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const messageContent = inputValue.trim();
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          content: messageContent
        })
      });

      const data = await response.json();
      
      // Conversation now exists
      setConversationExists(true);

      // Add user message
      const newMessages = [...messages, data.userMessage];
      
      // Add agent message if present
      if (data.agentMessage) {
        newMessages.push(data.agentMessage);
      }
      
      setMessages(newMessages);
      setStatus(data.status || status);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getStatusText = () => {
    switch (status) {
      case 'AGENT': return 'AI Assistant';
      case 'WAITING': return 'Connecting to staff...';
      case 'ASSIGNED': return 'Staff Online';
      case 'CLOSED': return 'Conversation Closed';
      default: return 'Support';
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button className="chat-button" onClick={toggleChat} aria-label="Open chat support">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {unreadCount > 0 && <span className="chat-badge">{unreadCount}</span>}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-header-info">
              <h3>TC Nails Support</h3>
              <span className="chat-status">{getStatusText()}</span>
            </div>
            <button className="chat-close" onClick={toggleChat}>×</button>
          </div>

          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="chat-welcome">
                <p>Welcome to TC Nails Depot support!</p>
                <p>How can we help you today?</p>
              </div>
            )}
            {messages.map((msg) => (
              <div key={msg.id} className={`chat-message ${msg.sender.toLowerCase()}`}>
                <div className="message-content">{msg.content}</div>
                <div className="message-time">{formatTime(msg.created_at)}</div>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message agent">
                <div className="message-content typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            <input
              type="text"
              className="chat-input"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading || status === 'CLOSED'}
            />
            <button 
              className="chat-send" 
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading || status === 'CLOSED'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
